import { useCustomMutation, useGetIdentity, useLogout } from "@refinedev/core";
import { NavLink, Outlet, useLocation } from "react-router";

import { LogOutIcon } from "@/components/Icon";
import { formatDateTime, roleLabel } from "@/lib/format";
import type { AdminIdentity } from "@/providers/authProvider";

interface NavItem {
  to: string;
  label: string;
  /** Pozycje bez uprawnienia znikają z menu - nie wyszarzamy ich na próżno. */
  permission?: string;
}

const NAV: NavItem[] = [
  { to: "/", label: "Przegląd" },
  { to: "/zamowienia", label: "Zamówienia", permission: "orders.read" },
  { to: "/opowiesci", label: "Opowieści", permission: "memoirs.read" },
  { to: "/pytania", label: "Baza pytań", permission: "prompts.read" },
  { to: "/wysylki-pytan", label: "Wysyłki pytań", permission: "deliveries.read" },
  { to: "/rozmowy", label: "Rozmowy telefoniczne", permission: "interviews.read" },
  { to: "/skrzynka", label: "Skrzynka do przypisania", permission: "inbox.read" },
  { to: "/historie", label: "Historie", permission: "stories.read" },
  { to: "/transkrypcje", label: "Kolejka transkrypcji", permission: "stories.read" },
  { to: "/przesylki", label: "Przesyłki", permission: "shipments.read" },
  { to: "/uzytkownicy", label: "Konta rodzin", permission: "users.read" },
  { to: "/rodziny", label: "Rodziny", permission: "users.read" },
  { to: "/system", label: "System", permission: "system.read" },
  { to: "/audyt", label: "Audyt", permission: "audit.read" },
];

export function Layout() {
  const { data: identity } = useGetIdentity<AdminIdentity | null>();
  const { mutate: logout } = useLogout();
  const location = useLocation();
  const permissions = identity?.permissions ?? [];

  return (
    <div className="flex min-h-screen items-stretch">
      <nav className="w-56 shrink-0 border-r border-line bg-surface p-5">
        <p className="font-display text-brand text-ink">Opowiedzo</p>
        <p className="mt-0.5 mb-6 text-base text-ink-soft">panel wewnętrzny</p>

        <ul className="flex flex-col gap-0.75">
          {NAV.filter((item) => !item.permission || permissions.includes(item.permission)).map(
            (item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.to === "/"}
                  className={({ isActive }) =>
                    `block rounded-card px-3 py-2 text-base transition-colors ${
                      isActive
                        ? "bg-accent/10 font-semibold text-accent"
                        : "text-ink hover:bg-line/40"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              </li>
            ),
          )}
        </ul>
      </nav>

      <div className="flex min-w-0 flex-1 flex-col">
        <ImpersonationBar identity={identity ?? null} />

        <header className="flex items-center justify-between gap-4 border-b border-line px-8 py-3.5">
          <div className="text-base">
            <span className="text-ink">{identity?.name ?? ""}</span>
            <span className="text-ink-soft"> - {roleLabel(identity?.role ?? null)}</span>
            {identity?.content_access && (
              // Wgląd w treści jest wyjątkiem, nie stanem domyślnym - ma być widoczny.
              <span className="ml-3 rounded-pill border border-gold/50 px-2.25 py-0.75 text-xs text-gold">
                wgląd w treści włączony
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={() => logout()}
            className="inline-flex items-center gap-1.5 rounded-card border border-line px-3.25 py-1.75 text-base text-ink transition-colors hover:border-ink-soft"
          >
            <LogOutIcon />
            Wyloguj
          </button>
        </header>

        {/* Klucz na ścieżce sprawia, że każde wejście w widok odtwarza
            animację. Ruszamy wyłącznie przezroczystością i przesunięciem. */}
        <main key={location.pathname} className="view-enter min-w-0 flex-1 px-8 pt-7 pb-12">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/**
 * Pasek podszycia się pod rodzinę. Siedzi w układzie, a nie na podstronie
 * użytkownika, bo wyjście musi być widoczne z każdego ekranu - podszycie,
 * o którym się zapomina, jest gorsze niż jego brak (spec.md rozdz. 8.3).
 */
function ImpersonationBar({ identity }: { identity: AdminIdentity | null }) {
  const { mutate, mutation } = useCustomMutation();
  const podszycie = identity?.impersonating;

  if (!podszycie) return null;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 bg-accent px-8 py-2.75 text-bg">
      <p className="text-base">
        Jesteś na koncie rodziny: <strong>{podszycie.user_name}</strong> ({podszycie.user_email}).
        Powód: {podszycie.reason}. Sesja wygasa {formatDateTime(podszycie.expires_at)}.
      </p>
      <button
        type="button"
        disabled={mutation.isPending}
        className="rounded-card bg-bg px-3.5 py-2 text-base font-semibold text-accent transition-opacity hover:opacity-90 disabled:opacity-60"
        onClick={() =>
          mutate(
            {
              url: "/impersonation/stop",
              method: "post",
              values: {},
              successNotification: false,
              errorNotification: false,
            },
            { onSuccess: () => window.location.reload() },
          )
        }
      >
        Zakończ i wróć do siebie
      </button>
    </div>
  );
}
